import { occupancyPct } from '@/pages/Hive/Tabs/Notices/NoticeDetails/PoleLimits/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ProfileUsers } from '../../../../../../api/enumerations';
import { fetchAllPages } from '../../../../../../api/fetchAllPages';
import { getPoleLimits } from '../../../../../../api/theHive/noticeCoverage';
import { PoleLimitData } from '../../../../../../api/theHive/noticeCoverage/types';
import { useCurrentRole } from '../../../../../../hooks/useCurrentRole';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';
import { PoleCapacity, UfCapacity } from './types';

// O edital Proto Network tem ~500 polos: buscar página única truncaria a
// contagem — o mesmo erro que inflava o contador da aba Mapa.
const PAGE_SIZE = 200;
const MAX_PAGES = 20;

const toPoleCapacity = (raw: PoleLimitData): PoleCapacity => ({
  id: raw.id,
  name: raw.name ?? raw.uf,
  uf: raw.uf,
  active: raw.active_accreditations,
  limit: raw.max_companies,
  availableSlots: raw.available_slots,
  waitlist: raw.waitlist_count,
  occupancy: occupancyPct(raw.active_accreditations, raw.max_companies),
});

interface UsePoleCapacityHook {
  poles: PoleCapacity[];
  ufs: UfCapacity[];
  /** `true` quando há vários editais e nenhum está selecionado. */
  needsNoticeSelection: boolean;
  /** `true` quando o edital tem mais polos do que o teto de carga. */
  truncated: boolean;
  loading: boolean;
}

export default function usePoleCapacity(): UsePoleCapacityHook {
  const role = useCurrentRole();
  const { selectedNoticeId, noticeOptions } =
    useOutletContext<RedeProtoOutletContext>();

  const [poles, setPoles] = useState<PoleCapacity[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const runRef = useRef(0);

  // Mesmo gate do hook irmão: o componente só redireciona role sem permissão no
  // render, então sem esta checagem o fetch dispararia antes do <Navigate>.
  const isAllowed =
    role === ProfileUsers.ADMIN || role === ProfileUsers.BANK_ADMIN;

  /**
   * `getPoleLimits` é escopado a um edital, mas o seletor global aceita "Todos
   * os editais" (default). Com um único edital cadastrado assumimos ele; com
   * vários, a seção pede a seleção em vez de mostrar dado de um edital só.
   */
  const effectiveNoticeId = useMemo(() => {
    if (selectedNoticeId != null) return selectedNoticeId;
    return noticeOptions.length === 1 ? noticeOptions[0].id : null;
  }, [selectedNoticeId, noticeOptions]);

  const fetchPoles = useCallback(async (noticeId: number) => {
    const runId = runRef.current + 1;
    runRef.current = runId;

    setLoading(true);
    try {
      const result = await fetchAllPages<PoleLimitData>(
        (pageNumber) => getPoleLimits(noticeId, pageNumber, PAGE_SIZE),
        { maxPages: MAX_PAGES }
      );
      if (runRef.current !== runId) return;
      setPoles(result.rows.map(toPoleCapacity));
      setTruncated(result.truncated);
    } catch (error) {
      if (runRef.current !== runId) return;
      setPoles([]);
      setTruncated(false);
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      if (runRef.current === runId) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAllowed || effectiveNoticeId == null) {
      setPoles([]);
      setTruncated(false);
      return;
    }
    fetchPoles(effectiveNoticeId);
  }, [isAllowed, effectiveNoticeId, fetchPoles]);

  const ufs = useMemo(() => {
    const byUf = new Map<string, UfCapacity>();

    poles.forEach((pole) => {
      const current = byUf.get(pole.uf) ?? {
        uf: pole.uf,
        poles: 0,
        active: 0,
        limit: 0,
        availableSlots: 0,
        waitlist: 0,
        occupancy: 0,
      };

      current.poles += 1;
      current.active += pole.active;
      current.limit += pole.limit;
      current.availableSlots += pole.availableSlots;
      current.waitlist += pole.waitlist;
      byUf.set(pole.uf, current);
    });

    return Array.from(byUf.values())
      .map((item) => ({
        ...item,
        occupancy: occupancyPct(item.active, item.limit),
      }))
      .sort((a, b) => b.waitlist - a.waitlist || a.uf.localeCompare(b.uf));
  }, [poles]);

  return {
    poles,
    ufs,
    needsNoticeSelection: isAllowed && effectiveNoticeId == null,
    truncated,
    loading,
  };
}
